import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  // Navigation items — Quickscan links to the intro page, "Doe de quickscan" jumps
  // straight into the assessment (highlighted as an accent-button below).
  const navItems = [
    { name: t('home'), path: '/' },
    { name: t('assessment'), path: '/quickscan' },
    { name: t('useCases'), path: '/use-cases' },
    { name: t('blog'), path: '/blog' },
  ];

  const isActive = (path) => location.pathname === path;

  const closeMenu = () => setMobileMenuOpen(false);

  const handleLogout = async () => {
    if (logout) {
      await logout();
      navigate('/');
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo (falls back to text placeholder until admin uploads one). Kept
              compact — h-9 — so nav items stay visually balanced. */}
          <Link to="/" className="flex items-center space-x-2" data-testid="site-logo-link">
            <Logo className="h-9 w-auto" variant="dark" placeholder="Humanistix" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={closeMenu}
                className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                  isActive(item.path) ? 'text-blue-500' : 'text-gray-700'
                }`}
                data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
              >
                {item.name}
              </Link>
            ))}

            {/* Highlighted CTA: skip the intro and go straight into the quickscan */}
            <Link
              to="/quickscan/start"
              onClick={closeMenu}
              className={`text-sm font-semibold px-4 py-2 rounded-lg transition-all shadow-sm ${
                isActive('/quickscan/start')
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
              data-testid="nav-do-quickscan"
            >
              {t('doQuickscan')} ›
            </Link>

            <LanguageSwitcher />

            {user ? (
              <div className="flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                    isActive('/dashboard') ? 'text-blue-500' : 'text-gray-700'
                  }`}
                >
                  {t('myDashboard')}
                </Link>
                <div className="flex items-center gap-2">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                  )}
                  <button
                    onClick={handleLogout}
                    className="text-gray-500 hover:text-red-500 transition-colors"
                    title={t('logout')}
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-sm font-medium text-gray-700 hover:text-blue-500 transition-colors"
                data-testid="login-link"
              >
                {t('login')}
              </Link>
            )}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              className="p-2 rounded-md text-gray-700 hover:text-blue-500"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-3 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={closeMenu}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-blue-500 bg-blue-50'
                    : 'text-gray-700 hover:text-blue-500 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/quickscan/start"
              onClick={closeMenu}
              className="block px-3 py-2 rounded-md text-base font-semibold bg-blue-500 text-white text-center"
              data-testid="nav-do-quickscan-mobile"
            >
              {t('doQuickscan')}
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMenu}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-500 hover:bg-gray-50"
                >
                  {t('myDashboard')}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMenu}
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-blue-500 hover:bg-gray-50 text-center"
              >
                {t('login')}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
